<!-- #region HTML -->
<template>
  <div class="app">
    <!-- #region Alert Component -->
    <alert :SystemErrorAlert="isSystemError" @update:SystemErrorAlert="isSystemError = $event"></alert>
    <!-- #endregion -->

    <!-- #region Main Container -->
    <div class="main-container form-container">
      <!-- #region Steps -->
      <div class="steps">
        <div class="right-box">
          <div class="sub-container">
            <!-- #region Title -->
            <div class="title">
              <label>パスワード変更</label>
            </div>
            <!-- #endregion -->
          </div>
        </div>
      </div>
      <!-- #endregion -->

      <!-- #region Form -->
      <form novalidate @submit.prevent="validateUser">
        <md-card class="form-container">
          <!-- #region Progress Bar -->
          <div class='progress-bar'>
            <md-progress-bar v-if="sending" md-mode="indeterminate" />
          </div>
          <!-- #endregion -->

          <!-- #region Old Password Field -->
          <div class="md-form">
            <md-field :class="getValidationClass('oldPassword')">
              <md-input id="old-password" v-model="form.oldPassword" name="old-password" :type="passwordOldInputType"
                placeholder="現在のパスワード ※8字以上64字以下" :disabled="sending" @input="clearErrorFlag" />
              <span class="toggle-password" @click="toggleOldPassword()">
                <img :src="passwordOldInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワードを表示" width="100%" />
              </span>
              <span v-if="!$v.form.oldPassword.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.oldPassword.minLength"
                class="md-error error mb-20">8文字以上、64文字以下で入力してください。</span>
              <span v-else-if="!$v.form.oldPassword.maxLength"
                class="md-error error mb-20">8文字以上、64文字以下で入力してください。</span>
              <span v-else-if="isInvalidOldPassword === true"
                class="md-error error mb-20 md-error-old-password ">現在のパスワードが誤っています。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region New Password Field -->
          <div class="md-form">
            <md-field :class="getValidationClass('newPassword')">
              <md-input id="new-password" v-model="form.newPassword" name="new-password" :type="passwordInputType"
                placeholder="新しいパスワード ※8字以上64字以下" :disabled="sending" :class="passwordStrengthClass"
                @input="checkPasswordStrength" />
              <div v-if="$v.form.newPassword.sameAsOldPassword" class="password-strength-bar">
                <span v-for="n in 5" :key="n" :class="getSegmentClass(n)" class="strength-segment"></span>
              </div>
              <div class="password-strength" :class="passwordStrengthClass">
                <span v-if="passwordStrengthMessage && $v.form.newPassword.sameAsOldPassword">{{
                  passwordStrengthMessage }}</span>
              </div>
              <span class="toggle-password" @click="togglePassword()">
                <img :src="passwordInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワードを表示" width="100%" />
              </span>
              <span v-if="!$v.form.newPassword.required" class="md-error error mb-10">入力してください。</span>
              <span v-else-if="!$v.form.newPassword.minLength"
                class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.newPassword.maxLength"
                class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.newPassword.sameAsOldPassword && $v.form.newPassword.$dirty"
                class="md-error error">現在のパスワードと新しいパスワードは、異なるものを入力してください。</span>
              <span v-else-if="showPasswordLengthError" class="md-error error mb-10">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="showSameAsOldPasswordError"
                class="md-error error">現在のパスワードと新しいパスワードは、異なるものを入力してください。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region Password Alert -->
          <div class="pw-alert" :class="passwordStrengthClass"
            v-if="form.newPassword.length > 7 && form.newPassword.length < 65 && $v.form.newPassword.sameAsOldPassword && (isPasswordAcceptable === true || isPasswordWeak === true)">
            <div class="alert-sp text-left">
              <div class="pw-warning" v-if="passwordWarning">{{ passwordWarning }}</div>
              <div v-if="passwordSuggestions.length > 0">
                <div class="text-left">
                  <ul class="password-suggestions">
                    <li v-for="(suggestion, index) in passwordSuggestions" :key="index"> {{ suggestion }} </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <!-- #endregion -->

          <!-- #region Confirm Password Field -->
          <div class="md-form">
            <md-field :class="getValidationClass('newPasswordConfirm')">
              <md-input id="new-password-confirm" v-model="form.newPasswordConfirm" name="new-password-confirm"
                :type="passwordConfirmInputType" placeholder="新しいパスワード ※8字以上64字以下" :disabled="sending"
                @focus="onPasswordConfirmFocus" />
              <span class="toggle-password" @click="togglePasswordConfirm()">
                <img :src="passwordConfirmInputType === 'text' ? eyeOpenImg : eyeCloseImg" alt="パスワード(確認)を表示"
                  width="100%" />
              </span>
              <span v-if="!$v.form.newPasswordConfirm.required" class="md-error error">入力してください。</span>
              <span v-else-if="!$v.form.newPasswordConfirm.minLength"
                class="md-error error">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.newPasswordConfirm.maxLength"
                class="md-error error">8文字以上、64文字以下で設定してください。</span>
              <span v-else-if="!$v.form.newPasswordConfirm.sameAsPassword"
                class="md-error error">新しいパスワードと新しいパスワード(確認)が、異なっています。</span>
            </md-field>
          </div>
          <!-- #endregion -->

          <!-- #region Submit Button -->
          <div class="submit-container">
            <md-card-actions>
              <md-button type="submit" class="primary-button" :disabled="sending || isPasswordWeak || !isFormValid">
                新しいパスワードを設定する
              </md-button>
            </md-card-actions>
          </div>
          <!-- #endregion -->
        </md-card>
      </form>
      <!-- #endregion -->
    </div>
    <!-- #endregion -->
  </div>
</template>
<!-- #endregion -->

<!-- #region JavaScript -->
<script>
// @ is an alias to /src
import Alert from '@/layout/Alerts.vue';
import { isFormValid, togglePassword, togglePasswordConfirm } from '@/utils/base.js';
import { checkPasswordStrength, getSegmentClass } from '@/utils/passwordUtils';
import { Auth } from 'aws-amplify';
import { validationMixin } from 'vuelidate';
import { maxLength, minLength, not, required, sameAs } from 'vuelidate/lib/validators';

let cognito_user
let cognito_username
const validPassword = async (value) => {
  try {
    const user = await Auth.signIn(cognito_username, value)
    cognito_user = user
    return true;
  } catch (error) {
    if (error.code === 'NotAuthorizedException') {
      return { success: false, errorType: 'invalidPassword' };
    }
    else {
      return { success: false, errorType: 'systemError' };
    }
  }
}

const includesAlphabetsAndNumbers = (value) => {
  const pattern = /^(?=.*?[a-z])(?=.*?\d)[a-z\d]{8,64}$/i
  return pattern.test(value)
}

export default {
  name: 'ChangePassword',
  mixins: [validationMixin],
  components: {
    "alert": Alert
  },
  props: {
    username: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    form: {
      oldPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
      username: '',
    },
    userSaved: false,
    sending: false,
    lastUser: null,
    message: '',
    isInvalidOldPassword: false,
    isSystemError: false,
    isPasswordWeak: false,
    eyeOpenImg: require('@/assets/img/eye-open.svg'),
    eyeCloseImg: require('@/assets/img/eye-close.svg'),
    passwordStrengthClass: '',
    passwordStrengthMessage: '',
    passwordWarning: '',
    passwordSuggestions: [],
    passwordStrengthScore: 0,
    passwordOldInputType: 'password',
    passwordInputType: 'password',
    passwordConfirmInputType: 'password',
    showPasswordLengthError: false,
    showSameAsOldPasswordError: false,
  }),
  created() {
    cognito_username = this.username
  },
  validations: {
    form: {
      oldPassword: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
      },
      newPassword: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
        sameAsOldPassword: not(sameAs('oldPassword')),
      },
      newPasswordConfirm: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(64),
        sameAsPassword: sameAs('newPassword'),
      },
    },
  },
  computed: {
    isFormValid() {
      return isFormValid(this.$v);
    },
  },
  methods: {
    getValidationClass(fieldName) {
      const field = this.$v.form[fieldName]
      if (fieldName == 'oldPassword') {
        if (this.isInvalidOldPassword) {
          return {
            'md-invalid': true
          }
        }
      }
      if (fieldName === 'newPassword' && (this.showPasswordLengthError || this.showSameAsOldPasswordError)) {
        return { 'md-invalid': true };
      }
      if (field) {
        if (field.$anyError) {
          this.isInvalidOldPassword = false
        }
      }
      return {
        'md-invalid': field.$invalid && field.$dirty,
      }
    },
    clearForm() {
      this.$v.$reset()
      this.form.oldPassword = null
      this.form.newPassword = null
      this.form.newPasswordConfirm = null
    },
    clearErrorFlag() {
      this.isSystemError = false;
      this.isInvalidOldPassword = false;
      this.showSameAsOldPasswordError = false;
    },
    async saveUser() {
      this.sending = true;
      try {
        await Auth.changePassword(cognito_user, this.form.oldPassword, this.form.newPassword);
        await Auth.signOut({ global: true });
        this.$store.commit('clearAuthData');
        this.$emit('callSignout');
        this.clearForm();
      } catch (error) {
        this.isSystemError = true
        this.sending = false
        window.scrollTo(0, 0);
        return
      }
      this.userSaved = true
      this.sending = false
      this.clearForm()
      this.$router.push({ name: 'completeChangePassword' })
    },
    async validateUser() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      const passwordValidationResult = await validPassword(this.form.oldPassword)
      if (passwordValidationResult.success === false) {
        switch (passwordValidationResult.errorType) {
          case 'invalidPassword':
            this.isInvalidOldPassword = true
            break;
          default:
            this.isSystemError = true;
            window.scrollTo(0, 0);
        }
        return;
      }
      this.checkPasswordStrength();
      if (this.isPasswordWeak) {
        this.isPasswordWeak = true;
        this.isAliasExists = false;
        this.isSystemError = false;
        window.scrollTo(0, 0);
        return;
      } else if (this.isPasswordAcceptable) {
        this.isPasswordAcceptable = true;
        this.isPasswordWeak = false;
      }
      this.isPasswordWeak = false;
      this.isInvalidOldPassword = false
      this.saveUser()
    },
    toggleOldPassword() {
      this.passwordOldInputType = this.passwordOldInputType === 'password' ? 'text' : 'password';
    },
    togglePassword() {
      this.passwordInputType = togglePassword(this.passwordInputType);
    },
    togglePasswordConfirm() {
      this.passwordConfirmInputType = togglePasswordConfirm(this.passwordConfirmInputType);
    },
    checkPasswordStrength() {
      const passwordStrength = checkPasswordStrength(this.form.newPassword);
      this.passwordStrengthClass = passwordStrength.class;
      this.passwordStrengthMessage = passwordStrength.message;
      this.passwordWarning = passwordStrength.warning;
      this.passwordSuggestions = passwordStrength.suggestions;
      this.passwordStrengthScore = passwordStrength.score;
      this.isPasswordWeak = passwordStrength.isPasswordWeak;
      this.isPasswordAcceptable = passwordStrength.isPasswordAcceptable;
      this.showPasswordLengthError = false;
      this.showSameAsOldPasswordError = false;
    },

    getSegmentClass(index) {
      return getSegmentClass(index, this.form.newPassword.length, this.passwordStrengthScore);
    },

    onPasswordConfirmFocus() {
      this.showPasswordLengthError = !this.$v.form.newPassword.minLength || !this.$v.form.newPassword.maxLength;
      this.showSameAsOldPasswordError = !this.$v.form.newPassword.sameAsOldPassword;
    },

  },
}
</script>
<!-- #endregion -->

<!-- #region CSS -->
<style>
.md-error-old-password {
  color: #ff1744 !important;
  display: block !important;
  opacity: 1 !important;
}
</style>
<!-- #endregion -->